import type { ChatSession } from "@lib/models/ChatSession";
import type { Interaction } from "@lib/models/Interaction";
import { ConnectionState } from "@lib/models/connectionState";
import { create, createStore, type StoreApi } from 'zustand'
import { RunMode, SkillLevel } from "@lib/models/enums";
import { devtools } from "zustand/middleware";
import type { ToolEventDetails } from "@lib/models/ServerEvent";
import type { InteractionStatistics } from "@lib/models/semanticKernelModel/models";
import type { AgentContext } from "@lib/models/contexts";
import type { ISessionService } from "@lib/services/servers/sessionService/ISessionService";

export type AiChatStore = StoreApi<AiChatState>;

export function createAiChatStore(sessionService: ISessionService, onRenderPrompt?: (prompt: string) => { promptAsString?: string, json?: string }): AiChatStore {
    return createStore<AiChatState>()(
        devtools(
            (set, get) => ({
                session: null,
                loading: false,
                bindingPromise: null,
                sessionActive: true,
                loadedInstructions: null,
                sendingMessage: false,
                lastActivity: new Date().getTime(),
                turnOpen: false,
                suspendState: 'idle' as SuspendState,
                message: null,
                messages: [],
                _turnCompleteCounter: 0,
                skillLevel: SkillLevel.LIVE,
                loadingType: null,
                onRenderPrompt,
                connectionStatus: ConnectionState.Disconnected,
                setSendingMessage: (sendingMessage: boolean) => {
                    set({ sendingMessage });
                },
                setLoadedInstructions: (_instructions: string[]) => { },
                currentMessage: null,
                setCurrentMessage: (currentMessage: Interaction | null) => {
                    set({ currentMessage });
                },
                setLastToolEvent: (toolEvent: ToolEventDetails) => {
                    set({ lastToolEvent: toolEvent }, false, "lastTooEvent");
                },
                resetMessages: () => {
                    const session = get().session;
                    set({
                        messages: [],
                        currentMessage: null,
                        ...(session && { session: { ...session, interactions: [] } }),
                    });
                },
                setIsSessionActive: (active: boolean) => {
                    set({ sessionActive: active });
                },
                setLastActivity: () => {
                    set({ lastActivity: new Date().getTime() }, false, "setLastActivity");
                },
                setNewMessage: (msg: Interaction) => {
                    let messages = get().messages;
                    msg.turn_complete = true;
                    const existingIndex = messages.findIndex((m) => m.interactionId == msg.interactionId);
                    if (existingIndex == -1) {
                        messages = [...messages, msg];
                    } else {
                        messages[existingIndex] = msg;
                        messages = [...messages];
                    }
                    const session = get().session;
                    set({
                        messages,
                        currentMessage: null,
                        _turnCompleteCounter: get()._turnCompleteCounter + 1,
                        ...(session && { session: { ...session, activityCount: session.activityCount + 1, interactions: messages } }),
                    });
                },
                setTurnOpen: (status: boolean) => {
                    // Closing the turn always invalidates suspend state —
                    // the agent isn't running anymore, so "suspended"
                    // would just freeze the prompt cluster on Continuar/
                    // Stop indefinitely. Reset to idle here instead of
                    // expecting every consumer to remember to do it.
                    if (status) {
                        set({ turnOpen: status });
                    } else {
                        set({ turnOpen: status, suspendState: 'idle' });
                    }
                },
                setSuspendState: (status: SuspendState) => set({ suspendState: status }),
                setConnectionStatus: (status: ConnectionState) => {
                    let loadingError = get().loadingError;
                    let loading = get().loading;
                    if (status == ConnectionState.Connected) {
                        loadingError = null;
                        loading = false;
                    }
                    set({ connectionStatus: status, loadingError, loading });
                },
                setStatistics: (stats) => {
                    set({ statistics: stats })
                },
                setPartialSession: (session: Partial<ChatSession>) => {
                    const updatedSession: ChatSession = { ...get().session, ...session as unknown as ChatSession };
                    set({ session: updatedSession });
                },
                setSession: (session: ChatSession | null) => {
                    if (session)
                        session.activityCount++;

                    set({ session, messages: session?.interactions ?? [], currentMessage: null });
                },
                subAgents: new Map<string, SubAgentState>(),
                setSubAgentMessage: (subAgentId: string, interaction: Interaction, name?: string) => {
                    const prev = get().subAgents;
                    const next = new Map(prev);
                    const existing = next.get(subAgentId);
                    next.set(subAgentId, {
                        id: subAgentId,
                        name: name ?? existing?.name ?? subAgentId,
                        interaction,
                        completed: existing?.completed ?? false,
                    });
                    set({ subAgents: next });

                },
                completeSubAgent: (subAgentId: string) => {
                    const prev = get().subAgents;
                    const existing = prev.get(subAgentId);
                    if (!existing) return;
                    const next = new Map(prev);
                    next.set(subAgentId, { ...existing, completed: true });
                    set({ subAgents: next });
                },
                removeSubAgent: (subAgentId: string) => {
                    const prev = get().subAgents;
                    if (!prev.has(subAgentId)) return;
                    const next = new Map(prev);
                    next.delete(subAgentId);
                    set({ subAgents: next });
                },
                setLoadingInformation: (state: ConnectionState, loading: boolean, loadingType?: 'EXISTING' | 'NEW', loadingError?: string) => {
                    set({ connectionStatus: state, loading, loadingType, loadingError: loadingError ?? get().loadingError });
                },
            } as AiChatState),
            { name: 'AiChatStore' }
        )
    );
}

export interface SubAgentState {
    id: string;
    name?: string;
    interaction: Interaction;
    completed: boolean;
}

/**
 * Per-turn suspend state shared by every UI surface that exposes a
 * pause/continue/stop control (PromptV2's send-button cluster,
 * RunProgressPanelV2's header). v1 kept this state inside the Prompt
 * component because that was the only place it surfaced; v2 ships
 * the same control in two places, so a local state slice would let
 * them disagree (pause in one surface didn't flip the icon in the
 * other). The state machine is:
 *
 *   idle ──pause()──> suspending ──ack──> suspended
 *                                            │
 *                                            ├── stop()       ──> idle (turn killed)
 *                                            ├── continue()   ──> idle (partial finalized)
 *                                            └── sendMessage  ──> idle (auto-cleared by gateway)
 *
 * Reset to `idle` whenever the turn closes so the cluster doesn't get
 * stuck on "Continuar" between turns.
 */
export type SuspendState = 'idle' | 'suspending' | 'suspended';

export interface AiChatState {
    session: ChatSession | null
    sendingMessage: boolean,
    loading: boolean,
    loadingError?: string | null,
    sessionActive: boolean,
    loadingType: null | 'NEW' | 'EXISTING'
    skillLevel: SkillLevel,
    turnOpen: boolean,
    suspendState: SuspendState,
    statistics?: InteractionStatistics,
    connectionStatus: ConnectionState,
    lastToolEvent?: ToolEventDetails,
    lastActivity: number,
    messages: Interaction[],
    currentMessage: Interaction | null,
    _turnCompleteCounter: number,
    subAgents: Map<string, SubAgentState>,
    setStatistics: (stats: InteractionStatistics) => void;
    setIsSessionActive: (active: boolean) => void,
    setLastActivity: () => void;
    setLoadingInformation: (state: ConnectionState, loading: boolean, loadingType: 'EXISTING' | 'NEW' | undefined, loadingError?: string) => void;
    setSendingMessage: (newValue: boolean) => void,
    setLoadedInstructions: (instructions: string[]) => void,
    setCurrentMessage: (message: Interaction | null) => void,
    setLastToolEvent: (ToolEventDetails: ToolEventDetails | null) => void,
    resetMessages: () => void;
    setNewMessage: (message: Interaction) => void,
    setTurnOpen: (status: boolean) => void,
    setSuspendState: (status: SuspendState) => void,
    setConnectionStatus: (status: ConnectionState) => void,
    setSubAgentMessage: (subAgentId: string, interaction: Interaction, name?: string) => void,
    completeSubAgent: (subAgentId: string) => void,
    removeSubAgent: (subAgentId: string) => void,
    bindingPromise: Promise<ChatSession | null> | null,
    setPartialSession: (session: Partial<ChatSession | null>) => void,
    setSession: (session: ChatSession | null) => void,
    onRenderPrompt?: (prompt: string) => { promptAsString?: string, json?: any }
}